import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Send cookies with requests
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add CSRF token to headers if available
    const csrfToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrfToken="))
      ?.split("=")[1];

    if (csrfToken) {
      config.headers["X-CSRF-TOKEN"] = csrfToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- Secure Axios Instance for new backend protocol ---
const axiosSecure = axios.create({
  baseURL: API_URL ? `${API_URL}/api` : "/api", // Use API_URL in development, relative in production
  withCredentials: true,
  timeout: 30000, // Increased timeout to 30 seconds for better reliability
});

// Request interceptor to add CSRF token and Authorization header
axiosSecure.interceptors.request.use(async (config) => {
  // Try to get Clerk token from window.__clerk if available
  let token = null;

  // Check if Clerk is available and get session token
  if (window.Clerk && window.Clerk.session) {
    try {
      token = await window.Clerk.session.getToken();
    } catch (error) {
      // Failed to get Clerk session token
    }
  }

  // Fallback to stored tokens
  if (!token) {
    const clerkToken = localStorage.getItem("clerkToken");
    const legacyToken = localStorage.getItem("token") || localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
    token = clerkToken || legacyToken;
  }

  if (token && token !== 'null') {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  // Add CSRF token for state-changing requests
  if (["post", "put", "patch", "delete"].includes(config.method)) {
    const csrfToken = document.cookie
      .split(";")
      .map((row) => row.trim())
      .find((row) => row.startsWith("csrfToken="));
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken.split("=")[1];
    }
  }

  // Add better retry and timeout handling for network issues
  config.timeoutErrorMessage = 'Request timeout - please check your network connection';

  return config;
});

// Response interceptor to handle auth errors and retries
axiosSecure.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Log network errors for debugging
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('Network timeout detected. Request may need to be retried with slower network conditions.');
    }

    // Allow individual requests to bypass the redirect logic
    const shouldRedirect = !error.config?.disableRedirect;

    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403) &&
      shouldRedirect
    ) {
      // Only redirect if not already on /admin/login
      if (window.location.pathname !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }

    return Promise.reject(error);
  }
);

// Public axios instance (no credentials) for anonymous endpoints like analytics visits
const axiosPublic = axios.create({
  baseURL: API_URL ? `${API_URL}/api` : "/api", // Use API_URL in development, relative in production
  withCredentials: false,
  timeout: 10000, // 10 second timeout for public requests
});

export { axiosSecure, axiosPublic };

export default api;
